// ✅ Trekkin.app — ImageUploader.jsx v2.1.0 (MULTI + MOBILE GALLERY)
// -----------------------------------------------------------------------------
// 🔹 Abre galeria no telemóvel (accept image/*)
// 🔹 Permite selecionar várias imagens (multiple)
// 🔹 Faz upload sequencial e chama onUploaded(url) por cada imagem enviada
// 🔹 Mantém validação (jpg/jpeg/png/webp) para bater certo com o server
// -----------------------------------------------------------------------------

import { useState } from "react";
import { showToast } from "./NotifyToast.jsx";
import { useLanguageStore } from "../stores/useLanguageStore";

const IMAGE_ENDPOINT = "https://www.trekkin.pt/app/app-upload/upload-image.php";
const MAX_UPLOAD_BYTES = Math.round(1.5 * 1024 * 1024);
const MAX_DIMENSION = 1400;
const isIOS =
  typeof navigator !== "undefined" &&
  /iPad|iPhone|iPod/i.test(navigator.userAgent || "");

export default function ImageUploader({
  eventId = "new",
  onUploaded,
  label = "Imagem do evento (foto de capa)",
  value = null,
  multiple = true, // 🆕 default ON
  maxFiles = null,
  hidePreview = false,
}) {
  const language = useLanguageStore((state) => state.language);
  const isEnglish = String(language || "").toLowerCase().startsWith("en");
  const tr = (pt, en) => isEnglish ? en : pt;
  const [isUploading, setIsUploading] = useState(false);
  const [fileName, setFileName] = useState("");
  const [previewUrl, setPreviewUrl] = useState(value || "");
  const [error, setError] = useState("");

  const loadImage = (file) =>
    new Promise((resolve, reject) => {
      const objectUrl = URL.createObjectURL(file);
      const img = new Image();

      img.onload = () => {
        URL.revokeObjectURL(objectUrl);
        resolve(img);
      };

      img.onerror = () => {
        URL.revokeObjectURL(objectUrl);
        reject(new Error(tr("Não foi possível ler a imagem.", "The image could not be read.")));
      };

      img.src = objectUrl;
    });

  const compressToWebp = async (file) => {
    const img = await loadImage(file);
    const baseName = file.name.replace(/\.[^.]+$/, "") || "imagem";
    const qualities = [0.8, 0.7, 0.62, 0.54, 0.46];
    const scales = [1, 0.85, 0.7, 0.6, 0.5];

    for (const scale of scales) {
      const ratio =
        Math.min(1, MAX_DIMENSION / Math.max(img.width, img.height)) * scale;
      const width = Math.max(1, Math.round(img.width * ratio));
      const height = Math.max(1, Math.round(img.height * ratio));

      const canvas = document.createElement("canvas");
      canvas.width = width;
      canvas.height = height;

      const ctx = canvas.getContext("2d");
      if (!ctx) throw new Error(tr("Falha ao processar imagem.", "Could not process the image."));
      ctx.drawImage(img, 0, 0, width, height);

      for (const quality of qualities) {
        const blob = await new Promise((resolve) =>
          canvas.toBlob(
            (b) => resolve(b),
            "image/webp",
            quality
          )
        );
        if (blob && blob.size <= MAX_UPLOAD_BYTES) {
          return new File([blob], `${baseName}.webp`, { type: "image/webp" });
        }
      }
    }

    throw new Error(tr("Imagem demasiado grande mesmo após compressão.", "The image is still too large after compression."));
  };

  const compressToJpeg = async (file) => {
    const img = await loadImage(file);
    const baseName = file.name.replace(/\.[^.]+$/, "") || "imagem";
    const qualities = [0.85, 0.75, 0.65, 0.55, 0.45];
    const scales = [1, 0.9, 0.75, 0.6, 0.5];

    for (const scale of scales) {
      const ratio =
        Math.min(1, MAX_DIMENSION / Math.max(img.width, img.height)) * scale;
      const width = Math.max(1, Math.round(img.width * ratio));
      const height = Math.max(1, Math.round(img.height * ratio));

      const canvas = document.createElement("canvas");
      canvas.width = width;
      canvas.height = height;

      const ctx = canvas.getContext("2d");
      if (!ctx) throw new Error(tr("Falha ao processar imagem.", "Could not process the image."));
      ctx.drawImage(img, 0, 0, width, height);

      for (const quality of qualities) {
        const blob = await new Promise((resolve) =>
          canvas.toBlob(
            (b) => resolve(b),
            "image/jpeg",
            quality
          )
        );
        if (blob && blob.size <= MAX_UPLOAD_BYTES) {
          return new File([blob], `${baseName}.jpg`, { type: "image/jpeg" });
        }
      }
    }

    throw new Error(tr("Imagem demasiado grande mesmo após compressão.", "The image is still too large after compression."));
  };

  const convertToJpeg = async (file, quality = 0.9) => {
    const img = await loadImage(file);
    const baseName = file.name.replace(/\.[^.]+$/, "") || "imagem";

    const canvas = document.createElement("canvas");
    canvas.width = img.width;
    canvas.height = img.height;

    const ctx = canvas.getContext("2d");
    if (!ctx) throw new Error(tr("Falha ao processar imagem.", "Could not process the image."));
    ctx.drawImage(img, 0, 0);

    const blob = await new Promise((resolve) =>
      canvas.toBlob(
        (b) => resolve(b),
        "image/jpeg",
        quality
      )
    );
    if (!blob) throw new Error(tr("Falha ao converter imagem.", "Could not convert the image."));
    return new File([blob], `${baseName}.jpg`, { type: "image/jpeg" });
  };

  const convertHeicToJpeg = (file) =>
    new Promise((resolve, reject) => {
      const objectUrl = URL.createObjectURL(file);
      const img = new Image();

      img.onload = () => {
        const canvas = document.createElement("canvas");
        canvas.width = img.width;
        canvas.height = img.height;

        const ctx = canvas.getContext("2d");
        if (!ctx) {
          URL.revokeObjectURL(objectUrl);
          reject(new Error(tr("Falha ao processar imagem.", "Could not process the image.")));
          return;
        }

        ctx.drawImage(img, 0, 0);
        canvas.toBlob(
          (blob) => {
            URL.revokeObjectURL(objectUrl);
            if (!blob) {
              reject(new Error(tr("Falha ao converter imagem HEIC.", "Could not convert the HEIC image.")));
              return;
            }
            resolve(blob);
          },
          "image/jpeg",
          0.9
        );
      };

      img.onerror = () => {
        URL.revokeObjectURL(objectUrl);
        reject(new Error(tr("Não foi possível ler a imagem HEIC.", "The HEIC image could not be read.")));
      };

      img.src = objectUrl;
    });

  const normalizeImageFile = async (file) => {
    const ext = file.name.split(".").pop()?.toLowerCase() || "";
    const allowed = ["jpg", "jpeg", "png", "webp", "heic", "heif"];
    const heicExt = ["heic", "heif"];
    const heicMime = [
      "image/heic",
      "image/heif",
      "image/heic-sequence",
      "image/heif-sequence",
    ];

    if (allowed.includes(ext) && file.size <= MAX_UPLOAD_BYTES) {
      if (isIOS && ext === "webp") {
        return convertToJpeg(file);
      }
      return file;
    }

    if (heicExt.includes(ext) || heicMime.includes(file.type)) {
      try {
        const jpegBlob = await convertHeicToJpeg(file);
        const safeName = file.name.replace(/\.[^.]+$/, "") || "imagem";
        const jpegFile = new File([jpegBlob], `${safeName}.jpg`, {
          type: "image/jpeg",
        });
        return jpegFile.size > MAX_UPLOAD_BYTES
          ? (isIOS ? compressToJpeg(jpegFile) : compressToWebp(jpegFile))
          : jpegFile;
      } catch {
        if (file.size <= MAX_UPLOAD_BYTES) {
          if (isIOS && ext === "webp") return convertToJpeg(file);
          return file;
        }
        return isIOS ? compressToJpeg(file) : compressToWebp(file);
      }
    }

    if (allowed.includes(ext)) {
      return isIOS ? compressToJpeg(file) : compressToWebp(file);
    }

    throw new Error(tr("Formato inválido. Use JPG, PNG ou WEBP.", "Invalid format. Use JPG, PNG or WEBP."));
  };

  const uploadOne = async (file) => {
    const formData = new FormData();
    formData.append("file", file);
    formData.append("eventId", String(eventId || "new"));

    const res = await fetch(IMAGE_ENDPOINT, {
      method: "POST",
      body: formData,
    });

    let json;
    try {
      json = await res.json();
    } catch (err) {
      console.error("❌ Erro ao ler JSON do upload de imagem:", err);
      throw new Error(tr("Resposta inválida do servidor de upload.", "Invalid response from the upload server."));
    }

    if (!res.ok || json?.error || json?.success === false) {
      const msg =
        json?.error || tr(`Falha ao enviar a imagem. Código HTTP: ${res.status}.`, `Could not upload the image. HTTP status: ${res.status}.`);
      throw new Error(msg);
    }

    const finalUrl = json?.url;
    if (!finalUrl) throw new Error(tr("Upload concluído mas sem URL devolvido.", "Upload completed but no URL was returned."));
    return finalUrl;
  };

  const handleFileChange = async (e) => {
    const selectedFiles = Array.from(e.target.files || []);
    if (!selectedFiles.length) return;

    const limit =
      Number.isFinite(maxFiles) && maxFiles > 0 ? Math.max(1, Math.floor(maxFiles)) : null;
    const files = limit ? selectedFiles.slice(0, limit) : selectedFiles;
    if (limit && selectedFiles.length > limit) {
      showToast(tr(`Só podes enviar até ${limit} imagem(ns) de cada vez.`, `You can upload up to ${limit} image(s) at a time.`), "warning");
    }

    setError("");
    setFileName(files.length === 1 ? files[0].name : tr(`${files.length} imagens`, `${files.length} images`));

    try {
      setIsUploading(true);

      // upload sequencial (mais estável em mobile/WKWebView)
      for (const f of files) {
        const normalized = await normalizeImageFile(f);
        const url = await uploadOne(normalized);

        // preview fica com a última enviada (só UI)
        setPreviewUrl(url);

        // callback por imagem
        if (onUploaded) await onUploaded(url);
      }

      showToast(tr("Imagens enviadas com sucesso. ✅", "Images uploaded successfully. ✅"), "success");
    } catch (err) {
      console.error("❌ Erro inesperado no upload de imagem:", err);
      const msg = String(err?.message || tr("Erro ao enviar a imagem.", "Error uploading the image."));
      setError(msg);
      showToast(msg, "error");
    } finally {
      setIsUploading(false);
      e.target.value = ""; // importante para permitir escolher as mesmas fotos outra vez
    }
  };

  return (
    <div
      className="tk-image-uploader"
      style={{
        borderRadius: 8,
        padding: 10,
        border: "1px solid rgba(255,255,255,0.15)",
        background: "rgba(0,0,0,0.25)",
        display: "flex",
        flexDirection: "column",
        gap: 6,
      }}
    >
      <div style={{ fontSize: 12, opacity: 0.9 }}>{label}</div>

      <label className={`tk-image-uploader-picker${isUploading ? " is-disabled" : ""}`}>
        <span>{isUploading ? tr("A enviar…", "Uploading…") : tr("Escolher fotografias", "Choose photos")}</span>
        <input
          type="file"
          accept="image/*"
          multiple={!!multiple}
          onClick={() => { window.trekkinFileType = "image"; }}
          onChange={handleFileChange}
          disabled={isUploading}
        />
      </label>

      {!fileName && <div style={{ fontSize: 11, opacity: 0.72 }}>{tr("Nenhum ficheiro selecionado", "No file selected")}</div>}

      {fileName && (
        <div style={{ fontSize: 11, opacity: 0.8 }}>
          {tr("Seleção", "Selection")}: <strong>{fileName}</strong>
          {isUploading && tr(" — a enviar…", " — uploading…")}
        </div>
      )}

      {previewUrl && !hidePreview && (
        <div
          style={{
            marginTop: 6,
            borderRadius: 6,
            overflow: "hidden",
            maxHeight: 140,
            border: "1px solid rgba(255,255,255,0.15)",
          }}
        >
          <img
            src={previewUrl}
            alt={tr("Pré-visualização da imagem", "Image preview")}
            style={{
              display: "block",
              width: "100%",
              height: "auto",
              objectFit: "cover",
            }}
          />
        </div>
      )}

      {error && (
        <div style={{ fontSize: 11, color: "#ffb3b3", marginTop: 4 }}>
          {error}
        </div>
      )}
    </div>
  );
}
